import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AdminSidebar } from '../../components/Sidebar';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Swords, Check, BookOpen, MessageCircle, Scale, Landmark, Star, Moon, Tag, BookMarked, Heart, Scroll, Building } from 'lucide-react';
import api from '../../utils/api';

const CAT_LUCIDE = {
  quran: BookOpen, hadith: MessageCircle, fiqh: Scale,
  islamic_history: Landmark, history: Landmark,
  names_of_allah: Star, allah: Star,
  prophets: Moon, prophet: Moon,
  aqeedah: BookMarked, akhlaq: Heart, seerah: Scroll, ibadah: Building,
  custom: Tag,
};
const CatIcon = ({ slug, size = 13 }) => {
  const Icon = CAT_LUCIDE[slug] || Tag;
  return <Icon size={size} strokeWidth={1.8} />;
};

const BG_THEMES = [
  { value: 'dark',   label: 'Dunkel',        bg: '#1C1C1E', accent: '#5B8A75' },
  { value: 'green',  label: 'Islamisch Grün', bg: '#0D2B1F', accent: '#5B8A75' },
  { value: 'blue',   label: 'Nacht-Blau',    bg: '#0D1B2A', accent: '#4A90D9' },
  { value: 'purple', label: 'Lila',          bg: '#1A0D2B', accent: '#9B59B6' },
  { value: 'brown',  label: 'Erdtöne',       bg: '#2A1E0D', accent: '#C17B5C' },
  { value: 'light',  label: 'Hell',          bg: '#F7F5F0', accent: '#5B8A75' },
];

const DIFFICULTY_MODES = [
  { value: 'easy',     label: 'Leicht',     desc: 'Alle leichten Fragen (30 Sek.)', color: '#5B8A75', bg: '#EAF2EE' },
  { value: 'medium',   label: 'Mittel',     desc: 'Alle mittleren Fragen (20 Sek.)', color: '#1A56DB', bg: '#E8F0FE' },
  { value: 'hard',     label: 'Schwer',     desc: 'Alle schweren Fragen (10 Sek.)', color: '#D94F3D', bg: '#FDECEA' },
  { value: 'combined', label: 'Kombiniert', desc: 'Fragen manuell auswählen', color: '#8B6A1C', bg: '#FBF5E6' },
];

const TIME_MAP = { easy: 'easy', medium: 'medium', hard: 'hard', combined: 'medium' };

const isNew = (id) => id === 'new';

export default function AdminQuizDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasFeature } = useAuth();
  const toast = useToast();
  const creating = isNew(id);

  const [form, setForm] = useState({
    title: '', description: '', welcomeMessage: '',
    difficultyMode: 'medium',
    minParticipants: 2,
    enableStreak: false, enable5050: false, soundParticipant: false, soundProjector: false, leaderboardParticipant: true, leaderboardProjector: true, recapEnabled: false,
    enableFamilyMode: false, enableSpectators: false, enableLearningMode: false,
    showAnswerImmediately: true,
    questionIds: [],
    bgColor: 'dark',
    bgImage: null,
    isDuel: false,
    duelMosqueName: '',
    duelMosqueId: null,
    duelMosqueLogo: null,
    numQuestions: '',
    timePerQuestion: '',
    scheduledAt: '',
  });
  const [duelMosqueSearch, setDuelMosqueSearch] = useState('');
  const [duelMosqueResults, setDuelMosqueResults] = useState([]);
  const [duelMosqueSearching, setDuelMosqueSearching] = useState(false);
  const [bgUploading, setBgUploading] = useState(false);
  const [pendingBgFile, setPendingBgFile] = useState(null);
  const [pendingBgPreview, setPendingBgPreview] = useState(null);
  const [allQuestions, setAllQuestions] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]); // empty = all
  const [loading, setLoading] = useState(!creating);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/categories').then(r => setCategories(r.data.categories || []));
    api.get('/questions?limit=500').then(r => setAllQuestions(r.data.questions || []));
    if (!creating) {
      api.get(`/admin/quizzes/${id}`).then(r => {
        const q = r.data.quiz;
        setForm({
          title: q.title, description: q.description || '', welcomeMessage: q.welcome_message || '',
          difficultyMode: 'combined',
          minParticipants: q.min_participants,
          enableStreak: q.enable_streak, enable5050: q.enable_50_50, soundParticipant: q.sound_participant === true, soundProjector: q.sound_projector === true, leaderboardParticipant: q.leaderboard_participant !== false, leaderboardProjector: q.leaderboard_projector !== false, recapEnabled: q.recap_enabled,
          enableFamilyMode: q.enable_family_mode, enableSpectators: q.enable_spectators,
          enableLearningMode: q.enable_learning_mode,
          showAnswerImmediately: q.show_answer_immediately ?? true,
          questionIds: (r.data.questions || []).map(q2 => q2.id),
          bgColor: q.bg_color || 'dark',
          bgImage: q.bg_image || null,
          isDuel: q.is_duel || false,
          duelMosqueName: q.duel_mosque_name_resolved || q.duel_mosque_name || '',
          duelMosqueId: q.duel_mosque_id || null,
          duelMosqueLogo: q.duel_mosque_logo || null,
          numQuestions: q.num_questions ? String(q.num_questions) : '',
          timePerQuestion: q.time_per_question ? String(q.time_per_question) : '',
          scheduledAt: q.scheduled_at ? new Date(q.scheduled_at).toISOString().slice(0, 16) : '',
        });
      }).finally(() => setLoading(false));
    }
  }, [id, creating]);

  const handle = (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(p => ({ ...p, [e.target.name]: val }));
  };

  const setDiffMode = (mode) => {
    setForm(p => ({ ...p, difficultyMode: mode, questionIds: [] }));
  };

  const uploadBgImage = async (file, quizId) => {
    if (!file) return null;
    setBgUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await api.post(`/admin/quizzes/${quizId}/bg-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data.imageUrl;
    } catch (err) {
      toast(err.response?.data?.error || 'Fehler beim Bild-Upload', 'error');
      return null;
    } finally {
      setBgUploading(false);
    }
  };

  const handleBgFilePick = (file) => {
    if (!file) return;
    if (!creating) {
      uploadBgImage(file, id).then(url => { if (url) setForm(p => ({ ...p, bgImage: url })); });
    } else {
      if (pendingBgPreview) URL.revokeObjectURL(pendingBgPreview);
      setPendingBgFile(file);
      setPendingBgPreview(URL.createObjectURL(file));
    }
  };

  const toggleQuestion = (qid) => {
    setForm(p => {
      const has = p.questionIds.includes(qid);
      return { ...p, questionIds: has ? p.questionIds.filter(x => x !== qid) : [...p.questionIds, qid] };
    });
  };

  const inSelectedCategory = (q) =>
    selectedCategoryIds.length === 0 || selectedCategoryIds.includes(q.category_id);

  const questionsForMode = (mode) =>
    allQuestions.filter(q => q.level === mode && inSelectedCategory(q));

  const filteredQuestions = allQuestions.filter(q => {
    if (!inSelectedCategory(q)) return false;
    if (!searchQ) return true;
    return q.text_de?.toLowerCase().includes(searchQ.toLowerCase()) ||
           q.text_al?.toLowerCase().includes(searchQ.toLowerCase());
  });

  const toggleCategory = (catId) => {
    setSelectedCategoryIds(prev => {
      const has = prev.includes(catId);
      const next = has ? prev.filter(x => x !== catId) : [...prev, catId];
      // Reset manual question selection when category filter changes (only in combined mode)
      if (form.difficultyMode === 'combined') {
        setForm(p => ({ ...p, questionIds: [] }));
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const mode = form.difficultyMode;
      const timeLevel = TIME_MAP[mode];

      let questionIds = form.questionIds;
      if (mode !== 'combined') {
        questionIds = questionsForMode(mode).map(q => q.id);
        if (!questionIds.length) {
          toast(`Keine ${DIFFICULTY_MODES.find(d => d.value === mode).label}en Fragen vorhanden`, 'error');
          setSaving(false);
          return;
        }
      }

      if (form.isDuel && !form.duelMosqueId) {
        toast('Bitte wählen Sie eine registrierte Gast-Moschee aus der Liste aus.', 'error');
        setSaving(false);
        return;
      }

      const body = {
        title: form.title,
        description: form.description || undefined,
        welcomeMessage: form.welcomeMessage || undefined,
        timeLevel,
        minParticipants: parseInt(form.minParticipants),
        enableStreak: form.enableStreak,
        enable5050: form.enable5050,
        enableFamilyMode: form.enableFamilyMode,
        enableSpectators: form.enableSpectators,
        enableLearningMode: form.enableLearningMode,
        showAnswerImmediately: form.showAnswerImmediately,
        soundParticipant: form.soundParticipant,
        soundProjector: form.soundProjector,
        leaderboardParticipant: form.leaderboardParticipant,
        leaderboardProjector: form.leaderboardProjector,
        recapEnabled: form.recapEnabled,
        bgColor: form.bgColor,
        bgImage: form.bgImage || undefined,
        questionIds,
        isDuel: form.isDuel,
        duelMosqueName: form.isDuel ? (form.duelMosqueName || null) : null,
        duelMosqueId: form.isDuel ? (form.duelMosqueId || null) : null,
        numQuestions: form.numQuestions ? parseInt(form.numQuestions) : null,
        timePerQuestion: form.timePerQuestion ? parseInt(form.timePerQuestion) : null,
        scheduledAt: form.scheduledAt || null,
      };

      if (creating) {
        const res = await api.post('/admin/quizzes', body);
        if (pendingBgFile && res.data?.quiz?.id) {
          const url = await uploadBgImage(pendingBgFile, res.data.quiz.id);
          if (url) await api.put(`/admin/quizzes/${res.data.quiz.id}`, { bgImage: url });
        }
        navigate('/admin/quizzes');
      } else {
        await api.put(`/admin/quizzes/${id}`, body);
        if (questionIds.length) {
          await api.post(`/admin/quizzes/${id}/questions`, { questionIds });
        }
        navigate('/admin/quizzes');
      }
    } catch (err) {
      toast(err.response?.data?.error || 'Fehler beim Speichern', 'error');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="spinner-wrap"><div className="spinner"/></div>;

  const selectedMode = DIFFICULTY_MODES.find(d => d.value === form.difficultyMode);
  const autoCount = form.difficultyMode !== 'combined' ? questionsForMode(form.difficultyMode).length : 0;

  return (
    <div style={{ display: 'flex' }}>
      <AdminSidebar />
      <main className="main-content">
        <div className="flex gap-3 items-center mb-6">
          <Link to="/admin/quizzes" className="btn btn-ghost btn-sm">← Zurück</Link>
          <h1 style={{ fontSize: 24 }}>{creating ? 'Quiz erstellen' : form.title}</h1>
        </div>

        <form onSubmit={submit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 860 }}>

            {/* Grundeinstellungen */}
            <div className="card">
              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 20 }}>
                Grundeinstellungen
              </h3>
              <div className="form-group">
                <label className="form-label">Titel *</label>
                <input className="form-input" name="title" value={form.title} onChange={handle} required minLength={3} placeholder="z.B. Ramadan Quiz 2026" />
              </div>
              <div className="grid-2" style={{ gap: 16 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Begrüßungsnachricht</label>
                  <textarea className="form-textarea" name="welcomeMessage" value={form.welcomeMessage} onChange={handle} rows={2} placeholder="Willkommensnachricht für Teilnehmer..." />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Mindest-Teilnehmer</label>
                  <input className="form-input" type="number" name="minParticipants" value={form.minParticipants} onChange={handle} min={1} max={500} />
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 16 }}>
                <label className="form-label">Quiz planen (optional)</label>
                <input
                  className="form-input"
                  type="datetime-local"
                  name="scheduledAt"
                  value={form.scheduledAt}
                  onChange={handle}
                  min={new Date().toISOString().slice(0, 16)}
                />
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                  Wenn gesetzt, erscheint das Quiz erst ab diesem Zeitpunkt in der Lobby.
                </div>
              </div>
            </div>

            {/* Schwierigkeitsmodus */}
            <div className="card">
              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 20 }}>
                Schwierigkeitsmodus
              </h3>
              <div className="grid-4" style={{ gap: 12 }}>
                {DIFFICULTY_MODES.map(mode => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setDiffMode(mode.value)}
                    style={{
                      padding: '16px 12px',
                      borderRadius: 10,
                      border: `2px solid ${form.difficultyMode === mode.value ? mode.color : 'var(--border)'}`,
                      background: form.difficultyMode === mode.value ? mode.bg : 'var(--surface)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 15, color: form.difficultyMode === mode.value ? mode.color : 'var(--text)', marginBottom: 4 }}>
                      {mode.label}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.4 }}>{mode.desc}</div>
                    {mode.value !== 'combined' && (
                      <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: mode.color }}>
                        {questionsForMode(mode.value).length} Fragen verfügbar
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {form.difficultyMode !== 'combined' && (
                <div style={{ marginTop: 16, padding: '12px 16px', background: selectedMode?.bg, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    background: form.difficultyMode === 'easy' ? '#5B8A75' : form.difficultyMode === 'medium' ? '#C9A84C' : '#D94F3D',
                  }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: selectedMode?.color }}>
                      {autoCount} {selectedMode?.label}e Fragen werden automatisch zugewiesen
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                      Antwortzeit: {form.difficultyMode === 'easy' ? '30' : form.difficultyMode === 'medium' ? '20' : '10'} Sekunden pro Frage
                    </div>
                  </div>
                </div>
              )}

              {/* Kategorienfilter */}
              {categories.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                    Kategorien filtern
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                    {/* "Alle" chip */}
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCategoryIds([]);
                        if (form.difficultyMode === 'combined') setForm(p => ({ ...p, questionIds: [] }));
                      }}
                      style={{
                        padding: '5px 13px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                        border: `1.5px solid ${selectedCategoryIds.length === 0 ? 'var(--primary)' : 'var(--border)'}`,
                        background: selectedCategoryIds.length === 0 ? 'var(--primary-lt)' : 'var(--surface)',
                        color: selectedCategoryIds.length === 0 ? 'var(--primary)' : 'var(--text3)',
                        cursor: 'pointer', transition: 'all 0.15s',
                      }}
                    >
                      Alle
                    </button>
                    {/* Global categories */}
                    {categories.filter(c => !c.mosque_id).map(cat => {
                      const active = selectedCategoryIds.includes(cat.id);
                      const count = allQuestions.filter(q => q.category_id === cat.id).length;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => toggleCategory(cat.id)}
                          style={{
                            padding: '5px 13px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                            border: `1.5px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                            background: active ? 'var(--primary-lt)' : 'var(--surface)',
                            color: active ? 'var(--primary)' : 'var(--text2)',
                            cursor: 'pointer', transition: 'all 0.15s',
                            display: 'flex', alignItems: 'center', gap: 5,
                          }}
                        >
                          <CatIcon slug={cat.icon} />
                          <span>{cat.name_de}</span>
                          <span style={{ fontSize: 11, opacity: 0.65, fontWeight: 400 }}>({count})</span>
                        </button>
                      );
                    })}
                    {/* Mosque-specific categories */}
                    {categories.filter(c => c.mosque_id).length > 0 && (
                      <>
                        <div style={{ width: '100%', height: 0 }} />
                        <div style={{ fontSize: 11, color: 'var(--text3)', alignSelf: 'center', marginRight: 4, fontWeight: 600 }}>
                          Eigene:
                        </div>
                        {categories.filter(c => c.mosque_id).map(cat => {
                          const active = selectedCategoryIds.includes(cat.id);
                          const count = allQuestions.filter(q => q.category_id === cat.id).length;
                          return (
                            <button
                              key={cat.id}
                              type="button"
                              onClick={() => toggleCategory(cat.id)}
                              style={{
                                padding: '5px 13px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                                border: `1.5px solid ${active ? '#8B6A1C' : 'var(--border)'}`,
                                background: active ? '#FBF5E6' : 'var(--surface)',
                                color: active ? '#8B6A1C' : 'var(--text2)',
                                cursor: 'pointer', transition: 'all 0.15s',
                                display: 'flex', alignItems: 'center', gap: 5,
                              }}
                            >
                              <CatIcon slug={cat.icon} />
                              <span>{cat.name_de}</span>
                              <span style={{ fontSize: 11, opacity: 0.65, fontWeight: 400 }}>({count})</span>
                            </button>
                          );
                        })}
                      </>
                    )}
                  </div>
                  {selectedCategoryIds.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
                      {(() => {
                        const total = allQuestions.filter(q => selectedCategoryIds.includes(q.category_id)).length;
                        return `${total} Fragen in den gewählten Kategorien`;
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* Anzahl der Fragen */}
              <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: 'var(--surface2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>Anzahl Fragen im Spiel</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                    Leer = alle {form.difficultyMode === 'combined' ? form.questionIds.length : autoCount} Fragen verwenden
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="number"
                    className="form-input"
                    style={{ width: 90, textAlign: 'center', fontWeight: 700, fontSize: 16 }}
                    min={1}
                    max={form.difficultyMode === 'combined' ? form.questionIds.length : autoCount}
                    value={form.numQuestions}
                    onChange={e => setForm(p => ({ ...p, numQuestions: e.target.value }))}
                    placeholder="Alle"
                  />
                  <span style={{ fontSize: 13, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                    von {form.difficultyMode === 'combined' ? form.questionIds.length : autoCount}
                  </span>
                </div>
              </div>

              {/* Antwortzeit manuell */}
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: 'var(--surface2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>Antwortzeit pro Frage</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                    Leer = automatisch nach Schwierigkeitsgrad ({form.difficultyMode === 'easy' ? '30' : form.difficultyMode === 'hard' ? '10' : '20'} Sek.)
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="number"
                    className="form-input"
                    style={{ width: 90, textAlign: 'center', fontWeight: 700, fontSize: 16 }}
                    min={5}
                    max={300}
                    value={form.timePerQuestion}
                    onChange={e => setForm(p => ({ ...p, timePerQuestion: e.target.value }))}
                    placeholder="Auto"
                  />
                  <span style={{ fontSize: 13, color: 'var(--text3)', whiteSpace: 'nowrap' }}>Sek.</span>
                </div>
              </div>
            </div>

            {/* Fragenauswahl (nur bei kombiniert) */}
            {form.difficultyMode === 'combined' && (
              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Fragen auswählen ({form.questionIds.length} ausgewählt)
                  </h3>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => setForm(p => ({ ...p, questionIds: filteredQuestions.map(q => q.id) }))}>
                      Alle auswählen
                    </button>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => setForm(p => ({ ...p, questionIds: [] }))}>
                      Alle abwählen
                    </button>
                  </div>
                </div>
                <input
                  className="form-input"
                  placeholder="Fragen durchsuchen..."
                  value={searchQ}
                  onChange={e => setSearchQ(e.target.value)}
                  style={{ marginBottom: 12 }}
                />
                <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                  {filteredQuestions.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>Keine Fragen gefunden</div>
                  ) : filteredQuestions.map(q => {
                    const checked = form.questionIds.includes(q.id);
                    return (
                      <label
                        key={q.id}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 12,
                          padding: '10px 14px',
                          borderBottom: '1px solid var(--border)',
                          cursor: 'pointer',
                          background: checked ? 'var(--primary-lt)' : 'transparent',
                          transition: 'background 0.1s',
                        }}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggleQuestion(q.id)} style={{ marginTop: 2, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{q.text_de}</div>
                          {q.text_al && <div style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic', marginTop: 2 }}>{q.text_al}</div>}
                          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{q.category_name_de || q.category_name}</span>
                            <span style={{
                              fontSize: 11, fontWeight: 600,
                              color: q.level === 'easy' ? '#5B8A75' : q.level === 'medium' ? '#1A56DB' : '#D94F3D',
                            }}>
                              {q.level === 'easy' ? 'Leicht' : q.level === 'medium' ? 'Mittel' : 'Schwer'}
                            </span>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Hintergrundfarbe / Bild */}
            <div className="card">
              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>
                Hintergrund Quiz
              </h3>

              {/* Color themes */}
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8, fontWeight: 600 }}>Farbthema</div>
              <div className="grid-3" style={{ gap: 8, marginBottom: 16 }}>
                {BG_THEMES.map(theme => {
                  const isSelected = form.bgColor === theme.value && !form.bgImage;
                  return (
                    <button
                      key={theme.value}
                      type="button"
                      onClick={() => setForm(p => ({ ...p, bgColor: theme.value, bgImage: null }))}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 10,
                        border: `2px solid ${isSelected ? theme.accent : 'transparent'}`,
                        background: theme.bg,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        transition: 'border-color 0.15s',
                        outline: 'none',
                      }}
                    >
                      <div style={{ width: 20, height: 20, borderRadius: 5, background: theme.accent, flexShrink: 0 }} />
                      <div style={{ fontSize: 12, fontWeight: 600, color: theme.value === 'light' ? '#1C1C1E' : '#fff', flex: 1, textAlign: 'left' }}>
                        {theme.label}
                      </div>
                      {isSelected && <Check size={13} color={theme.accent} />}
                    </button>
                  );
                })}
              </div>

              {/* Image upload */}
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  Hintergrundbild (optional — uberschreibt Farbthema)
                  {!hasFeature('background_image') && (
                    <span style={{ fontSize: 10, background: '#F3F4F6', color: '#6b7280', padding: '2px 7px', borderRadius: 100, fontWeight: 700 }}>
                      Standard+
                    </span>
                  )}
                </div>
                {hasFeature('background_image') ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {(form.bgImage || pendingBgPreview) && (
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <img
                          src={pendingBgPreview || form.bgImage}
                          alt="Hintergrund"
                          style={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 8, border: '2px solid var(--primary)' }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setForm(p => ({ ...p, bgImage: null }));
                            setPendingBgFile(null);
                            if (pendingBgPreview) { URL.revokeObjectURL(pendingBgPreview); setPendingBgPreview(null); }
                          }}
                          style={{
                            position: 'absolute', top: -6, right: -6,
                            width: 18, height: 18, borderRadius: '50%',
                            background: 'var(--error)', color: '#fff',
                            border: 'none', cursor: 'pointer', fontSize: 11,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >x</button>
                      </div>
                    )}
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 16px', borderRadius: 8,
                      border: '1.5px dashed var(--border)',
                      cursor: bgUploading ? 'default' : 'pointer',
                      fontSize: 13, color: 'var(--text3)',
                      opacity: bgUploading ? 0.6 : 1,
                    }}>
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        disabled={bgUploading}
                        onChange={e => handleBgFilePick(e.target.files[0])}
                      />
                      {bgUploading ? 'Uploading...' : 'Bild hochladen'}
                    </label>
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: '#9ca3af', padding: '8px 0' }}>
                    Hintergrundbilder sind ab dem Standard-Plan verfugbar.{' '}
                    <a href="https://ilmify.ch/#pricing" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontWeight: 600 }}>
                      Upgraden
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Optionen */}
            <div className="card">
              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>
                Erweiterte Optionen
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {[
                  { name: 'enableStreak',         label: 'Treffserie aktivieren',   desc: 'Bonuspunkte für mehrere richtige Antworten in Folge (3x: +50%, 5x: +100%)' },
                  { name: 'enable5050',           label: '50/50-Hilfe aktivieren',  desc: 'Teilnehmer können einmalig zwei falsche Antworten ausblenden lassen' },
                  { name: 'enableSpectators',     label: 'Zuschauer erlauben',      desc: 'Zuschauer können das Quiz live mitverfolgen, ohne selbst mitzuspielen' },
                  { name: 'enableLearningMode',   label: 'Lernmodus (vor Quiz)',    desc: 'Teilnehmer sehen islamische Wissenskarten während sie auf den Quiz-Start warten' },
                  { name: 'showAnswerImmediately', label: 'Antwort sofort anzeigen', desc: 'Teilnehmer sehen das Ergebnis sofort nach der Antwort. Deaktiviert: Ergebnis wird erst nach Ablauf der Zeit für alle gleichzeitig angezeigt' },
                  { name: 'soundParticipant', label: 'Sound-Effekte — Teilnehmer', desc: 'Toene bei Fragenstart und Aufloesung auf den Teilnehmer-Geraeten (Handy) abspielen' },
                  { name: 'soundProjector', label: 'Sound-Effekte — Projektor', desc: 'Toene bei Fragenstart und Aufloesung auf der Projektor-Anzeige (Gast/Admin) abspielen' },
                  { name: 'leaderboardParticipant', label: 'Zwischenrangliste — Teilnehmer', desc: 'Rangliste nach jeder Frage bei den Teilnehmern (Handy) zeigen' },
                  { name: 'leaderboardProjector', label: 'Zwischenrangliste — Projektor', desc: 'Rangliste nach jeder Frage auf der Projektor-Anzeige (Gast) zeigen' },
                  { name: 'recapEnabled', label: 'Rueckblick nach Quiz', desc: 'Teilnehmer sehen am Ende alle Fragen mit den richtigen Antworten' },
                ].map((opt, i, arr) => (
                  <label key={opt.name} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    cursor: 'pointer', padding: '12px 0',
                    borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
                  }}>
                    <input type="checkbox" name={opt.name} checked={form[opt.name]} onChange={handle} style={{ marginTop: 3, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{opt.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, lineHeight: 1.4 }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Duell-Modus — Premium only */}
            <div className="card">
              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Swords size={13} /> Duell-Modus
                  <span style={{ fontSize: 10, background: '#8B6A1C22', color: '#8B6A1C', borderRadius: 4, padding: '2px 6px', fontWeight: 700 }}>Premium</span>
                </span>
              </h3>
              {!hasFeature('duels') ? (
                <div style={{ fontSize: 13, color: 'var(--text3)', padding: '10px 14px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, verticalAlign: 'middle' }}><Swords size={13} /></span>{' '}
                  Duell-Modus ist nur im <strong>Premium-Plan</strong> verfügbar.{' '}
                  <a href="/admin/profile#plan" style={{ color: 'var(--primary)' }}>Jetzt upgraden →</a>
                </div>
              ) : (<>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', marginBottom: form.isDuel ? 16 : 0 }}>
                <input type="checkbox" name="isDuel" checked={form.isDuel} onChange={handle} style={{ marginTop: 3, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>Duell zwischen Moscheen aktivieren</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, lineHeight: 1.4 }}>
                    Teilnehmer wählen ihr Team beim Beitritt. Am Ende wird ein Team-Ergebnis angezeigt.
                  </div>
                </div>
              </label>
              {form.isDuel && (
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Gast-Moschee auswählen</label>
                  {form.duelMosqueId ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, border: '2px solid var(--primary)', background: 'var(--primary-lt)', marginBottom: 8 }}>
                      {form.duelMosqueLogo
                        ? <img src={form.duelMosqueLogo} alt="" style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'contain', background: '#fff' }} />
                        : <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16 }}>{form.duelMosqueName?.[0] || '?'}</div>
                      }
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: 14 }}>{form.duelMosqueName}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>Registrierte Ilmify-Moschee</div>
                      </div>
                      <button type="button" onClick={() => setForm(p => ({ ...p, duelMosqueId: null, duelMosqueName: '', duelMosqueLogo: null }))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 18, lineHeight: 1 }}>✕</button>
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <input
                        className="form-input"
                        value={duelMosqueSearch}
                        onChange={e => {
                          setDuelMosqueSearch(e.target.value);
                          if (e.target.value.length >= 1) {
                            setDuelMosqueSearching(true);
                            api.get(`/admin/mosques/search?q=${encodeURIComponent(e.target.value)}`)
                              .then(r => setDuelMosqueResults(r.data.mosques || []))
                              .finally(() => setDuelMosqueSearching(false));
                          } else {
                            setDuelMosqueResults([]);
                          }
                        }}
                        placeholder="Moschee suchen..."
                        required={form.isDuel}
                      />
                      {(duelMosqueResults.length > 0 || duelMosqueSearching) && (
                        <div style={{ position: 'absolute', zIndex: 50, top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden' }}>
                          {duelMosqueSearching && <div style={{ padding: '10px 14px', color: 'var(--text3)', fontSize: 13 }}>Suche...</div>}
                          {duelMosqueResults.map(m => (
                            <button key={m.id} type="button"
                              onClick={() => {
                                setForm(p => ({ ...p, duelMosqueId: m.id, duelMosqueName: m.name, duelMosqueLogo: m.logo_path || null }));
                                setDuelMosqueSearch('');
                                setDuelMosqueResults([]);
                              }}
                              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderBottom: '1px solid var(--border)' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-lt)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            >
                              {m.logo_path
                                ? <img src={m.logo_path} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'contain', background: '#fff', border: '1px solid var(--border)' }} />
                                : <div style={{ width: 32, height: 32, borderRadius: 6, background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{m.name[0]}</div>
                              }
                              <div>
                                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{m.name}</div>
                                {m.city && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{m.city}</div>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>
                    Nur registrierte Ilmify-Moscheen können als Duell-Partner gewählt werden.
                  </div>
                </div>
              )}
              </>)}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary btn-lg" type="submit" disabled={saving}>
                {saving ? 'Speichern...' : creating ? 'Quiz erstellen' : 'Speichern'}
              </button>
              <Link to="/admin/quizzes" className="btn btn-ghost btn-lg">Abbrechen</Link>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
